import clevertap from 'clevertap-web-sdk';
import { devLog, stagingLog, prodLog } from '../utils/logUtils';

class CleverTapService {
  constructor() {
    this.isInitialized = false;
    this.accountId = null;
    this.region = null;
    this.environment = null;
  }

  // Get environment variables based on current environment
  getEnvironmentConfig() {
    const currentEnv = process.env.REACT_APP_ENV || 'development';
    this.environment = currentEnv;
    
    let accountId, region;
    
    if (currentEnv === 'production') {
      accountId = process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID_PROD;
      region = process.env.REACT_APP_CLEVERTAP_REGION_PROD || 'eu1';
    } else if (currentEnv === 'staging') {
      accountId = process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID_STAGING;
      region = process.env.REACT_APP_CLEVERTAP_REGION_STAGING || 'eu1';
    } else {
      accountId = process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID_DEV;
      region = process.env.REACT_APP_CLEVERTAP_REGION_DEV || 'eu1';
    }
    
    return { accountId, region };
  }

  // Initialize CleverTap
  initialize() {
    if (this.isInitialized) {
      console.log('CleverTap already initialized');
      return;
    }

    // Get environment configuration
    const { accountId, region } = this.getEnvironmentConfig();

    if (!accountId) {
      console.warn(`CleverTap Account ID not found for environment: ${this.environment}`);
      return;
    }

    this.accountId = accountId;
    this.region = region;

    try {
      devLog('Initializing CleverTap with:', {
        accountId: this.accountId,
        region: this.region,
        environment: this.environment
      });
      stagingLog('Initializing CleverTap with:', {
        accountId: this.accountId,
        region: this.region,
        environment: this.environment
      });

      // Enable CleverTap debug logs
      if (typeof clevertap.setLogLevel === 'function') {
        clevertap.setLogLevel(3); // 3 = debug
      } else {
        clevertap.debug = true;
      }

      // Try initializing without region first (some accounts don't need it)
      try {
        clevertap.init(this.accountId);
        console.log('CleverTap initialized without region');
      } catch (regionError) {
        console.log('Failed to initialize without region, trying with region:', this.region);
        clevertap.init(this.accountId, this.region);
        console.log('CleverTap initialized with region');
      }
      
      this.isInitialized = true;
      console.log('CleverTap initialized successfully');
      
      // // Test connection by sending a test event
      // this.testConnection();
    } catch (error) {
      console.error('Error initializing CleverTap:', error);
    }
  }

  // Track page views
  trackPageView(pageName, properties = {}) {
    if (!this.isInitialized) {
      console.warn('CleverTap not initialized');
      return;
    }

    try {
      console.log('Tracking page view:', pageName);
      clevertap.event.push('Page View', {
        'Page Name': pageName,
        'Page URL': window.location.href,
        'Referrer': document.referrer,
        'App Version': process.env.REACT_APP_VERSION || '1.0.0',
        'Platform': 'Web',
        'Environment': process.env.REACT_APP_ENV || 'development',
        ...properties
      });
      console.log('Page view event sent to CleverTap');
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  // Track user events
  trackEvent(eventName, properties = {}) {
    if (!this.isInitialized) {
      console.warn('CleverTap not initialized');
      return;
    }

    try {
      // console.log('Tracking event:', eventName, properties);
      clevertap.event.push(eventName, {
        'Timestamp': new Date().toISOString(),
        'User Agent': navigator.userAgent,
        'App Version': process.env.REACT_APP_VERSION || '1.0.0',
        'Platform': 'Web',
        'Environment': process.env.REACT_APP_ENV || 'development',
        ...properties
      });
      devLog('Event sent to CleverTap:', eventName);
      stagingLog('Event sent to CleverTap:', eventName);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  // Set user profile
  setUserProfile(profileData) {
    if (!this.isInitialized) {
      console.warn('CleverTap not initialized');
      return;
    }

    try {
      // console.log('Setting user profile:', profileData);
      clevertap.profile.push({
        "Site": {
          'Name': profileData.name || '',
          'Email': profileData.email || '',
          'Encrypted Phone': profileData.phone || '',
          'User ID': profileData.id || '',
          'First Name': profileData.firstName || '',
          'Last Name': profileData.lastName || '',
          'City': profileData.city || '',
          'State': profileData.state || '',
          'Location': profileData.location || '',
          'Experience Level': profileData.experienceLevel || ''
        }
      });
      devLog('User profile set in CleverTap');
      stagingLog('User profile set in CleverTap');
    } catch (error) {
      console.error('Error setting user profile:', error);
    }
  } 

  // Track error events
  trackError(errorType, properties = {}) {
    this.trackEvent('Error Occurred', {
      'Error Type': errorType,
      'Error Message': properties.errorMessage || '',
      'Screen': properties.screen || '',
      'User Action': properties.userAction || '',
      ...properties
    });
  }

}

// Create singleton instance
const clevertapService = new CleverTapService();

export default clevertapService; 